import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Mail, Phone, Loader2, CheckCircle2, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { API_BASE_URL } from '@/config/api';

interface SenderValidationModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  senderEmail: string;
  senderMobile: string;
  onVerified: () => void;
}

type ModalStep = 'choose' | 'enter-code' | 'success';

const SenderValidationModal: React.FC<SenderValidationModalProps> = ({
  open,
  onOpenChange,
  senderEmail,
  senderMobile,
  onVerified,
}) => {
  const { t } = useTranslation('quicksend');

  const [step, setStep] = useState<ModalStep>('choose');
  const [method, setMethod] = useState<'email' | 'sms' | null>(null);
  const [token, setToken] = useState('');
  const [code, setCode] = useState('');
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState('');

  const resetState = () => {
    setStep('choose');
    setMethod(null);
    setToken('');
    setCode('');
    setSending(false);
    setVerifying(false);
    setError('');
  };

  const handleSendCode = async (via: 'email' | 'sms') => {
    setSending(true);
    setError('');
    setMethod(via);

    try {
      const body: Record<string, string> = { method: via };
      if (via === 'email') {
        body.email = senderEmail;
      } else {
        body.phone = senderMobile;
      }

      const response = await fetch(`${API_BASE_URL}/guest/verify/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || 'Failed to send code');
      }

      const result = await response.json();
      setToken(result.token);
      setStep('enter-code');
    } catch (err: any) {
      setError(err.message || 'Failed to send verification code');
    } finally {
      setSending(false);
    }
  };

  const handleVerify = async () => {
    if (code.length !== 6) return;

    setVerifying(true);
    setError('');

    try {
      const response = await fetch(`${API_BASE_URL}/guest/verify/check`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, code }),
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || t('validate_error'));
      }

      setStep('success');
      setTimeout(() => {
        onVerified();
        onOpenChange(false);
        resetState();
      }, 1500);
    } catch (err: any) {
      setError(err.message || t('validate_error'));
    } finally {
      setVerifying(false);
    }
  };

  const handleResend = () => {
    setCode('');
    setError('');
    if (method) handleSendCode(method);
  };

  const maskedDestination =
    method === 'email'
      ? senderEmail.replace(/(.{2})(.*)(@.*)/, '$1***$3')
      : senderMobile.replace(/(.{3})(.*)(.{4})/, '$1****$3');

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) resetState();
        onOpenChange(isOpen);
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('validate_title')}</DialogTitle>
          {step === 'choose' && (
            <DialogDescription>{t('validate_description')}</DialogDescription>
          )}
        </DialogHeader>

        {/* Step 1: Choose method */}
        {step === 'choose' && (
          <div className="space-y-3 pt-2">
            <Button
              variant="outline"
              className="w-full justify-start gap-3 h-14"
              onClick={() => handleSendCode('email')}
              disabled={sending}
            >
              {sending && method === 'email' ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <Mail className="h-5 w-5 text-blue-600" />
              )}
              <div className="text-left">
                <div className="font-medium">{t('validate_via_email')}</div>
                <div className="text-xs text-muted-foreground">{senderEmail}</div>
              </div>
            </Button>

            {senderMobile && (
              <Button
                variant="outline"
                className="w-full justify-start gap-3 h-14"
                onClick={() => handleSendCode('sms')}
                disabled={sending}
              >
                {sending && method === 'sms' ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <Phone className="h-5 w-5 text-green-600" />
                )}
                <div className="text-left">
                  <div className="font-medium">{t('validate_via_sms')}</div>
                  <div className="text-xs text-muted-foreground">{senderMobile}</div>
                </div>
              </Button>
            )}

            {error && <p className="text-sm text-red-600">{error}</p>}
          </div>
        )}

        {/* Step 2: Enter code */}
        {step === 'enter-code' && (
          <div className="space-y-4 pt-2">
            <p className="text-sm text-muted-foreground">
              {method === 'email'
                ? t('validate_email_sent', { destination: maskedDestination })
                : t('validate_sms_sent', { destination: maskedDestination })}
            </p>

            <div>
              <label className="text-sm font-medium mb-1.5 block">
                {t('validate_enter_code')}
              </label>
              <Input
                value={code}
                onChange={(e) => {
                  const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                  setCode(val);
                  setError('');
                }}
                placeholder={t('validate_code_placeholder')}
                className="text-center text-2xl tracking-[0.5em] font-mono"
                maxLength={6}
                autoFocus
              />
            </div>

            {error && <p className="text-sm text-red-600">{error}</p>}

            <div className="flex gap-2">
              <Button
                onClick={handleVerify}
                disabled={code.length !== 6 || verifying}
                className="flex-1"
              >
                {verifying ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    {t('validate_verifying')}
                  </>
                ) : (
                  t('validate_verify')
                )}
              </Button>
            </div>

            <div className="flex justify-between text-sm">
              <button
                type="button"
                className="text-muted-foreground hover:text-foreground underline-offset-4 hover:underline"
                onClick={() => {
                  setStep('choose');
                  setCode('');
                  setError('');
                }}
              >
                <ArrowLeft className="h-3 w-3 inline mr-1" />
                {t('validate_back')}
              </button>
              <button
                type="button"
                className="text-primary hover:underline underline-offset-4"
                onClick={handleResend}
                disabled={sending}
              >
                {t('validate_resend')}
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Success */}
        {step === 'success' && (
          <div className="flex flex-col items-center py-6 gap-3">
            <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircle2 className="h-8 w-8 text-green-600" />
            </div>
            <p className="font-medium text-green-700">{t('validate_success')}</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SenderValidationModal;
